import React, { useMemo } from 'react';
import { CheckCircle2, FileInput, BookOpen, GitMerge, ShieldCheck, Sparkles, type LucideIcon } from 'lucide-react';
import { PIPELINE_NODES, type NodeState } from './analysisContent';

interface PipelineGraphProps {
  nodeStates: Map<string, NodeState>;
  phaseTimings: Map<string, { startMs: number; endMs?: number }>;
}

/*
 * Segments map the 7 internal pipeline nodes into 5 user-facing stages.
 * Each segment derives its state from the "most advanced" state of its
 * constituent nodes: completed > active > pending > failed.
 */
interface Segment {
  label: string;
  description: string;
  icon: LucideIcon;
  /** Pipeline node IDs whose states feed into this segment */
  nodeIds: string[];
  colour: { pending: string; active: string; completed: string };
  iconColour: { pending: string; active: string; completed: string };
}

export const SEGMENTS: Segment[] = [
  {
    label: 'Ingest',
    description: 'Extracting text from your documents',
    icon: FileInput,
    nodeIds: ['ingest'],
    colour: { pending: 'bg-gray-200 dark:bg-medical-600', active: 'bg-teal-400 dark:bg-teal-500', completed: 'bg-teal-500 dark:bg-teal-400' },
    iconColour: { pending: 'text-gray-300 dark:text-medical-600', active: 'text-teal-400 dark:text-teal-400', completed: 'text-teal-500 dark:text-teal-400' },
  },
  {
    label: 'Review',
    description: 'AI is reading each document page by page',
    icon: BookOpen,
    nodeIds: ['deep-read', 'vision'],
    colour: { pending: 'bg-gray-200 dark:bg-medical-600', active: 'bg-cyan-400 dark:bg-cyan-500', completed: 'bg-cyan-500 dark:bg-cyan-400' },
    iconColour: { pending: 'text-gray-300 dark:text-medical-600', active: 'text-cyan-400 dark:text-cyan-400', completed: 'text-cyan-500 dark:text-cyan-400' },
  },
  {
    label: 'Cross-reference',
    description: 'Merging findings and building your chronology',
    icon: GitMerge,
    nodeIds: ['intelligence', 'chronicle'],
    colour: { pending: 'bg-gray-200 dark:bg-medical-600', active: 'bg-violet-400 dark:bg-violet-500', completed: 'bg-violet-500 dark:bg-violet-400' },
    iconColour: { pending: 'text-gray-300 dark:text-medical-600', active: 'text-violet-400 dark:text-violet-400', completed: 'text-violet-500 dark:text-violet-400' },
  },
  {
    label: 'Verify',
    description: 'Linking every finding to its source page',
    icon: ShieldCheck,
    nodeIds: ['cite'],
    colour: { pending: 'bg-gray-200 dark:bg-medical-600', active: 'bg-amber-400 dark:bg-amber-500', completed: 'bg-amber-500 dark:bg-amber-400' },
    iconColour: { pending: 'text-gray-300 dark:text-medical-600', active: 'text-amber-400 dark:text-amber-400', completed: 'text-amber-500 dark:text-amber-400' },
  },
  {
    label: 'Analyse',
    description: 'Generating case analyses from your evidence',
    icon: Sparkles,
    nodeIds: ['analyses'],
    colour: { pending: 'bg-gray-200 dark:bg-medical-600', active: 'bg-emerald-400 dark:bg-emerald-500', completed: 'bg-emerald-500 dark:bg-emerald-400' },
    iconColour: { pending: 'text-gray-300 dark:text-medical-600', active: 'text-emerald-400 dark:text-emerald-400', completed: 'text-emerald-500 dark:text-emerald-400' },
  },
];

export function deriveSegmentState(nodeIds: string[], nodeStates: Map<string, NodeState>): NodeState {
  const states = nodeIds.map(id => nodeStates.get(id) ?? 'pending');
  if (states.every(s => s === 'completed')) return 'completed';
  if (states.some(s => s === 'active')) return 'active';
  if (states.some(s => s === 'completed')) return 'active'; // partially done = still active
  if (states.some(s => s === 'failed')) return 'failed';
  return 'pending';
}

export const PipelineGraph: React.FC<PipelineGraphProps> = ({
  nodeStates,
}) => {
  const segmentStates = useMemo(() =>
    SEGMENTS.map(seg => deriveSegmentState(seg.nodeIds, nodeStates)),
    [nodeStates],
  );

  // Find the active (or most recently active) segment for status message
  const activeIndex = segmentStates.lastIndexOf('active');
  const statusSegment = activeIndex >= 0
    ? SEGMENTS[activeIndex]
    : segmentStates.every(s => s === 'completed')
      ? null
      : SEGMENTS[0];

  return (
    <div className="w-full space-y-2">
      {/* Stage icons */}
      <div className="flex gap-1">
        {SEGMENTS.map((seg, i) => {
          const state = segmentStates[i];
          const Icon = seg.icon;
          const iconClass = state === 'completed'
            ? seg.iconColour.completed
            : state === 'active'
              ? seg.iconColour.active
              : seg.iconColour.pending;

          return (
            <div key={seg.label} className="flex-1 flex justify-center">
              <Icon className={`w-5 h-5 transition-colors duration-700 ${iconClass}`} strokeWidth={1.5} />
            </div>
          );
        })}
      </div>

      {/* Segmented bar */}
      <div className="flex gap-1 h-2.5 rounded-full overflow-hidden">
        {SEGMENTS.map((seg, i) => {
          const state = segmentStates[i];
          const colourClass = state === 'completed'
            ? seg.colour.completed
            : state === 'active'
              ? seg.colour.active
              : seg.colour.pending;

          return (
            <div
              key={seg.label}
              className={`flex-1 rounded-full transition-all duration-700 ease-out ${colourClass} ${
                state === 'active' ? 'animate-pulse' : ''
              }`}
            />
          );
        })}
      </div>

      {/* Stage labels */}
      <div className="flex gap-1">
        {SEGMENTS.map((seg, i) => {
          const state = segmentStates[i];
          const isActive = state === 'active';
          const isCompleted = state === 'completed';

          return (
            <div key={seg.label} className="flex-1 flex items-center justify-center gap-1">
              {isCompleted && (
                <CheckCircle2 className="w-3 h-3 text-green-500 dark:text-green-400 flex-shrink-0" />
              )}
              {isActive && (
                <span className="relative flex h-1.5 w-1.5 flex-shrink-0">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-cyan-500" />
                </span>
              )}
              <span className={`text-xs truncate ${
                isActive
                  ? 'font-semibold text-gray-800 dark:text-medical-100'
                  : isCompleted
                    ? 'font-medium text-gray-500 dark:text-medical-400'
                    : 'text-gray-400 dark:text-medical-500'
              }`}>
                {seg.label}
              </span>
            </div>
          );
        })}
      </div>

    </div>
  );
};
